import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  FaChevronLeft,
  FaChevronRight,
  FaMinus,
  FaPlus,
  FaTimes,
} from "react-icons/fa";

export default function ReportPhotoViewer({
  items,
  index,
  onClose,
  onIndexChange,
}) {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [drag, setDrag] = useState(null);
  const total = Array.isArray(items) ? items.length : 0;
  const safeIndex = total ? ((index % total) + total) % total : 0;
  const current = total ? items[safeIndex] : null;
  const hasNav = total > 1;

  const zoomIn = () =>
    setZoom((value) => Math.min(4, Math.round((value + 0.25) * 100) / 100));
  const zoomOut = () =>
    setZoom((value) => {
      const next = Math.max(1, Math.round((value - 0.25) * 100) / 100);
      if (next === 1) setPan({ x: 0, y: 0 });
      return next;
    });
  const zoomReset = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const go = useCallback(
    (dir, event) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      if (total < 2) return;
      onIndexChange((safeIndex + dir + total) % total);
    },
    [onIndexChange, safeIndex, total]
  );

  useEffect(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [safeIndex]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === "function") {
          event.stopImmediatePropagation();
        }
        onClose();
        return;
      }
      if (event.key === "ArrowRight") go(1, event);
      if (event.key === "ArrowLeft") go(-1, event);
      if (event.key === "+" || event.key === "=") zoomIn();
      if (event.key === "-" || event.key === "_") zoomOut();
      if (event.key === "0") zoomReset();
    };
    document.addEventListener("keydown", onKey, true);
    const html = document.documentElement;
    const previousOverflow = html.style.overflow;
    html.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey, true);
      html.style.overflow = previousOverflow;
    };
  }, [go, onClose]);

  if (!current?.src || typeof document === "undefined") return null;

  return createPortal(
    <div className="dd-photo-viewer" role="dialog" aria-modal="true">
      <header className="dd-photo-viewer__bar">
        <div className="dd-photo-viewer__copy">
          <span>{current.symbol || "Photo"}</span>
          <strong>{current.label || "Inspection photo"}</strong>
        </div>
        <p className="dd-photo-viewer__count">
          {safeIndex + 1} / {total}
        </p>
        <button
          type="button"
          className="dd-photo-viewer__icon"
          onPointerDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onClose();
          }}
          aria-label="Close"
        >
          <FaTimes />
        </button>
      </header>

      <div
        className="dd-photo-viewer__stage"
        onWheel={(event) => {
          event.preventDefault();
          if (event.deltaY < 0) zoomIn();
          else zoomOut();
        }}
        onPointerDown={(event) => {
          if (zoom <= 1) return;
          if (event.target.closest("button")) return;
          event.currentTarget.setPointerCapture(event.pointerId);
          setDrag({
            x: event.clientX - pan.x,
            y: event.clientY - pan.y,
          });
        }}
        onPointerMove={(event) => {
          if (!drag) return;
          setPan({
            x: event.clientX - drag.x,
            y: event.clientY - drag.y,
          });
        }}
        onPointerUp={() => setDrag(null)}
        onPointerCancel={() => setDrag(null)}
      >
        <img
          src={current.src}
          alt={current.label || "Inspection photo"}
          draggable="false"
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            cursor: zoom > 1 ? (drag ? "grabbing" : "grab") : "zoom-in",
          }}
          onClick={(event) => {
            event.stopPropagation();
            if (zoom === 1) zoomIn();
          }}
        />
      </div>

      {hasNav ? (
        <button
          type="button"
          className="dd-photo-viewer__nav dd-photo-viewer__nav--prev"
          onClick={(event) => go(-1, event)}
          aria-label="Previous photo"
        >
          <FaChevronLeft />
        </button>
      ) : null}
      {hasNav ? (
        <button
          type="button"
          className="dd-photo-viewer__nav dd-photo-viewer__nav--next"
          onClick={(event) => go(1, event)}
          aria-label="Next photo"
        >
          <FaChevronRight />
        </button>
      ) : null}

      <footer className="dd-photo-viewer__tools">
        {hasNav ? (
          <button
            type="button"
            onClick={(event) => go(-1, event)}
            aria-label="Previous photo"
          >
            <FaChevronLeft />
          </button>
        ) : null}
        <button
          type="button"
          onClick={zoomOut}
          disabled={zoom <= 1}
          aria-label="Zoom out"
        >
          <FaMinus />
        </button>
        <button
          type="button"
          className="dd-photo-viewer__zoom-label"
          onClick={zoomReset}
          aria-label="Reset zoom"
        >
          {Math.round(zoom * 100)}%
        </button>
        <button
          type="button"
          onClick={zoomIn}
          disabled={zoom >= 4}
          aria-label="Zoom in"
        >
          <FaPlus />
        </button>
        {hasNav ? (
          <button
            type="button"
            onClick={(event) => go(1, event)}
            aria-label="Next photo"
          >
            <FaChevronRight />
          </button>
        ) : null}
      </footer>
    </div>,
    document.body
  );
}
